/**
 * Emits voice-recording analytics events.
 *
 * Shaped like useOnboardingEvents.js: a frozen name map plus one thin wrapper
 * per event over the shared gtag transport, so the event names and payload
 * shapes for this feature live in one file and callers cannot drift.
 *
 * All four names are declared here up front, even though the recorder wires
 * them in over three separate changes, so that later work extends this module
 * instead of inventing a second spelling for the same event.
 *
 * Nothing here carries audio, transcript text or dialog content. `emit` never
 * throws and silently drops events when `window.gtag` is absent, so telemetry
 * can never break a recording.
 */
import { emit } from "@/composables/analyticsTransport.js";

const EVENT_NAMES = Object.freeze({
  // Whether the screen wake lock could be taken for this recording.
  KEEP_AWAKE: "recording_keep_awake",
  // The recording hit the length ceiling and was held.
  CEILING_HOLD: "recording_ceiling_hold",
  // Capture was interrupted (backgrounded, track ended, track muted) and the
  // recording was held.
  INTERRUPTION_HOLD: "recording_interruption_hold",
  // What the learner did with a held recording.
  HELD_OUTCOME: "recording_held_outcome",
});

/**
 * "ios" | "android" | "web". Derived here rather than at each call site so
 * every event in this family reports it the same way.
 */
function currentPlatform() {
  if (typeof window === "undefined") return "web";
  const platform = window.Capacitor?.getPlatform?.();
  return typeof platform === "string" && platform ? platform : "web";
}

export function useRecordingEvents() {
  return {
    /**
     * Fired once per recording, at the moment the lock is asked for.
     * @param {{outcome: "acquired"|"unsupported"|"failed", platform?: string}} payload
     */
    trackKeepAwake(payload) {
      emit(EVENT_NAMES.KEEP_AWAKE, {
        outcome: (payload?.outcome || "").toLowerCase(),
        platform: (payload?.platform || currentPlatform()).toLowerCase(),
      });
    },

    /**
     * The recording reached the maximum length and was held for the learner to
     * send or discard. Carries no dimensions of its own: paired with
     * trackHeldOutcome it is the whole funnel for the ceiling.
     */
    trackCeilingHold() {
      emit(EVENT_NAMES.CEILING_HOLD, {});
    },

    /**
     * Capture was interrupted and the recording was held.
     * @param {{signal: string}} payload `signal` names which of the
     *   interruption signals fired (hidden, appStateChange, ended, mute).
     */
    trackInterruptionHold(payload) {
      emit(EVENT_NAMES.INTERRUPTION_HOLD, {
        signal: (payload?.signal || "").toLowerCase(),
      });
    },

    /**
     * What became of a held recording.
     * @param {{outcome: "sent"|"discarded"}} payload
     */
    trackHeldOutcome(payload) {
      emit(EVENT_NAMES.HELD_OUTCOME, {
        outcome: (payload?.outcome || "").toLowerCase(),
      });
    },
  };
}

export { EVENT_NAMES };
